import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Host, Listen, State, h, Element, Prop, Watch, Event, EventEmitter } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { ThemeConfig } from '@nylas/core';
/**
 * The `nylas-reminder-time` component is a form input for the reminder time before an event.
 *
 * @part nrt - The event reminder container
 * @part nrt__number-dropdown - The number dropdown container
 * @part nrt__number-dropdown-button - The number dropdown button
 * @part nrt__number-dropdown-content - The number dropdown content
 * @part nrt__period-dropdown - The period dropdown container
 * @part nrt__period-dropdown-button - The period dropdown button
 * @part nrt__period-dropdown-content - The period dropdown content
 * @part nrt__time-period-selector - The time period selector
 */
@Component({
  tag: 'nylas-reminder-time',
  styleUrl: 'nylas-reminder-time.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasReminderTime {
  /**
   * The host element <nylas-reminder-time>
   */
  @Element() host!: HTMLElement;
  /**
   * The name of the reminder time. Default is 'reminder time'.
   */
  @Prop() name: string = 'reminder-time';
  /**
   * The event reminder time in minutes as set in the configuration.
   */
  @Prop() eventReminderTimeMinutes?: number;
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;
  /**
   * The selected number for the reminder time.
   */
  @State() selectedNumber;

  /**
   * The selected period for the reminder time.
   */
  @State() selectedPeriod;

  /**
   * The error state to show the error message when the minutes selected is invalid (not divisible by 5).
   */
  @State() errorState: boolean = false;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-reminder-time', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('eventReminderTimeMinutes')
  eventReminderTimeMinutesChangedHandler(newValue: number, _oldValue: number) {
    debug('nylas-reminder-time', 'eventReminderTimeMinutesChangedHandler', newValue);
    this.updateNumberAndPeriodFromMinutes(newValue);
    this.isInternalsAvailable && this.internals.setFormValue(newValue?.toString(), this.name);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  /**
   * Event emitted when the event reminder time changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  connectedCallback() {
    debug('nylas-reminder-time', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    debug('nylas-reminder-time', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-reminder-time', 'componentDidLoad');
    this.updateNumberAndPeriodFromMinutes(this.eventReminderTimeMinutes || 30);
  }

  disconnectedCallback() {
    debug('nylas-reminder-time', 'disconnectedCallback');
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasSuggestReminderMinutes', { target: 'document' })
  nylasSuggestReminderMinutesHandler(event: CustomEvent<{ minutes: number }>) {
    const { minutes } = event.detail;
    this.errorState = false;
    this.valueChanged.emit({ value: minutes.toString(), name: this.name });
    this.isInternalsAvailable && this.internals.setFormValue(minutes.toString(), this.name);
    // Workaround: Force the time-period-selector to the next rounded multiple of 5
    // instead of the current rounded multiple. This handles cases where the user re-enters
    // the same number, ensuring the selector correctly updates to the intended rounded value.
    this.updateNumberAndPeriodFromMinutes(minutes + 7);
    setTimeout(() => {
      this.updateNumberAndPeriodFromMinutes(minutes);
    }, 10);
  }

  @Listen('timePeriodChanged')
  timePeriodChangedHandler(
    event: CustomEvent<{
      number: number;
      period: string;
    }>,
  ) {
    const { number, period } = event.detail;
    let totalMinutes;
    const minutesInHour = 60;
    const minutesInDay = minutesInHour * 24;
    const minutesInWeek = minutesInDay * 7;
    const minutesInMonth = minutesInDay * 30; // Assuming 30 days in a month for simplification

    if (period === 'minute') {
      totalMinutes = number;
    } else if (period === 'hour') {
      totalMinutes = number * minutesInHour;
    } else if (period === 'day') {
      totalMinutes = number * minutesInDay;
    } else if (period === 'week') {
      totalMinutes = number * minutesInWeek;
    } else if (period === 'month') {
      totalMinutes = number * minutesInMonth;
    } else {
      totalMinutes = 0;
    }
    this.errorState = totalMinutes % 5 !== 0;
    this.valueChanged.emit({ value: totalMinutes, name: this.name });
    this.isInternalsAvailable && this.internals.setFormValue(totalMinutes.toString(), this.name);
  }

  private updateNumberAndPeriodFromMinutes(minutes: number): void {
    const minutesInHour = 60;
    const minutesInDay = minutesInHour * 24;
    const minutesInWeek = minutesInDay * 7;
    const minutesInMonth = minutesInDay * 30; // Assuming 30 days in a month for simplification
    let number, period;

    if (minutes === 0) {
      number = 0;
      period = 'minute';
    } else if (minutes >= minutesInMonth && minutes % minutesInMonth === 0) {
      number = Math.floor(minutes / minutesInMonth);
      period = 'month';
    } else if (minutes >= minutesInWeek && minutes % minutesInWeek === 0) {
      number = Math.floor(minutes / minutesInWeek);
      period = 'week';
    } else if (minutes >= minutesInDay && minutes % minutesInDay === 0) {
      number = Math.floor(minutes / minutesInDay);
      period = 'day';
    } else if (minutes >= minutesInHour && minutes % minutesInHour === 0) {
      number = Math.floor(minutes / minutesInHour);
      period = 'hour';
    } else {
      number = minutes;
      period = 'minute';
    }

    this.selectedNumber = number;
    this.selectedPeriod = period;
  }

  /**
   * The unit tests in stencil does not support the internals API.
   * This method checks if the internals API is available.
   * @returns boolean
   */
  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function' && typeof this.internals.setValidity === 'function';
  }

  @RegisterComponent<NylasReminderTime, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-reminder-time',
    stateToProps: new Map([
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {},
    fireRegisterEvent: true,
  })
  render() {
    const showTimePeriodSelector = typeof this.selectedNumber == 'number' && typeof this.selectedPeriod == 'string';
    return (
      <Host>
        <div class="nylas-reminder-time" part="nrt">
          <div class="nylas-reminder-time__wrapper">
            {showTimePeriodSelector && (
              <time-period-selector
                id="limit-future-bookings"
                part="nrt__time-period-selector"
                hasError={this.errorState}
                timePeriods={[
                  { value: 'minute', label: i18next.t('timeDuration', { context: 'minute' }) },
                  { value: 'hour', label: i18next.t('timeDuration', { context: 'hour' }) },
                  { value: 'day', label: i18next.t('timePeriod', { context: 'day' }) },
                ]}
                exportparts="tps__number-dropdown: nrt__number-dropdown, tps__number-dropdown-button: nrt__number-dropdown-button, tps__number-dropdown-content: nrt__number-dropdown-content, tps__period-dropdown: nrt__period-dropdown, tps__period-dropdown-button: nrt__period-dropdown-button, tps__period-dropdown-content: nrt__period-dropdown-content"
                defaultSelectedNumber={this.selectedNumber}
                defaultSelectedPeriod={this.selectedPeriod}
                themeConfig={this.themeConfig}
              />
            )}
          </div>
        </div>
      </Host>
    );
  }
}
